import { createStyles, Group, Stack } from '@mantine/core';
import { AddFilter, Filter } from '@repo/common/components/Filter';
import type {
	FilterOperator,
	FilterOption,
} from '@repo/common/components/Filter/types.ts';
import { type ReactNode, useMemo } from 'react';
import type { FilterValue } from '../../../../Filter';

/** FilterValue.value can be string or string[] or null so normalize to string[] */
function toFilterValueStrings(val: FilterValue['value'] | undefined): string[] {
	if (val === undefined || val === null) return [];
	return Array.isArray(val) ? val.map(String) : [String(val)];
}

interface IntegrationResourceFiltersProps {
	filters: Record<string, string[]>;
	handleUpdate: (key: string, newFilters: string[]) => void;
	description?: ReactNode;
	filterOptions: { [key: string]: FilterOption };
}

const useStyles = createStyles((theme) => ({
	card: {
		padding: theme.spacing.md,
		border: `1px solid ${theme.other.getColor('border/secondary/default')}`,
		borderRadius: theme.radius.md,
	},
}));

export function IntegrationResourceFilters({
	filters,
	handleUpdate,
	description,
	filterOptions,
}: IntegrationResourceFiltersProps) {
	const { classes } = useStyles();

	const filterValues = useMemo(
		() =>
			Object.entries(filters ?? {})
				.filter(
					([key, values]) => !!filterOptions[key] && (values ?? []).length > 0
				)
				.map(([key, values]) => ({
					key,
					value: values,
					operator: filterOptions[key].filterDropdownConfig.defaultOperator,
					filterType: filterOptions[key].type,
				})),
		[filters, filterOptions]
	);

	const handleOnChange =
		(previousValues: (typeof filterValues)[number]) =>
		(value: Partial<FilterValue>) => {
			const newValues = toFilterValueStrings(value.value);
			handleUpdate(previousValues.key, newValues);
		};

	const handleOnClear = (values: (typeof filterValues)[number]) => () => {
		handleUpdate(values.key, []);
	};

	const handleOnAddFilter = (value: FilterValue) => {
		const key = value.filterType as string;
		const current = filters[key] ?? [];
		const toAdd = toFilterValueStrings(value.value);
		handleUpdate(key, [...current, ...toAdd]);

		const option = filterOptions[key];
		const updatedValue: FilterValue = {
			filterType: value.filterType,
			value: [...current, ...toAdd],
			operator: option?.filterDropdownConfig?.defaultOperator ?? value.operator,
		};
		return {
			value: updatedValue,
			changeFilter: handleOnChange({
				key,
				filterType: value.filterType,
				value: updatedValue.value as string[],
				operator: updatedValue.operator,
			}),
			clearFilter: handleOnClear({
				key,
				filterType: value.filterType,
				value: updatedValue.value as string[],
				operator: updatedValue.operator,
			}),
		};
	};

	return (
		<Stack spacing="lg">
			<Stack spacing="md" className={classes.card}>
				{description && (
					<Group position="apart" spacing={0}>
						{description}
					</Group>
				)}
				<Group spacing="xs">
					{filterValues.map((item, idx) => (
						<Filter
							key={`filter-${idx}`}
							filterOption={filterOptions[item.filterType]}
							value={{
								filterType: item.filterType,
								value: item.value,
								operator: item.operator,
							}}
							onChange={handleOnChange(item)}
							onClear={handleOnClear(item)}
							showDetailedLabel
							operatorConfig={{
								getOperators: () => [item.operator],
								getLabel: (operator: FilterOperator) => operator.toString(),
							}}
						/>
					))}
					<AddFilter
						options={Object.values(filterOptions)}
						onAddFilter={handleOnAddFilter}
					/>
				</Group>
			</Stack>
		</Stack>
	);
}
